import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { AdminLayout } from '../../layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { StatusBadge } from '../../components/LiveIndicator';
import { format } from 'date-fns';
import { 
  Plus, 
  Trophy,
  Trash2,
  Users,
  Loader2,
  Clock,
  CalendarIcon,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = '/api';

// Default UK/Irish venues
const DEFAULT_VENUES = [
  'Leopardstown', 'Cheltenham', 'Aintree', 'Ascot', 'Curragh', 'Punchestown',
  'Fairyhouse', 'Galway', 'Sandown', 'Newmarket', 'Epsom', 'York', 'Doncaster',
  'Kempton', 'Lingfield', 'Newcastle', 'Haydock', 'Goodwood', 'Chester', 'Newbury'
];

// Hours/Minutes for time picker
const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

function VenueComboBox({ value, onChange, existingVenues }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value || '');
  const inputRef = useRef(null);

  const allVenues = [...new Set([...DEFAULT_VENUES, ...existingVenues])].sort();
  const filtered = search
    ? allVenues.filter(v => v.toLowerCase().includes(search.toLowerCase()))
    : allVenues;

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Type or select venue..."
        className="bg-black border-zinc-800 text-zinc-100 pr-8"
        data-testid="venue-input"
      />
      <ChevronDown
        className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 cursor-pointer"
        onClick={() => { setOpen(!open); inputRef.current?.focus(); }}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto bg-zinc-900 border border-zinc-800 rounded-md shadow-lg">
          {filtered.map(venue => (
            <div
              key={venue}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-zinc-800 ${
                venue === value ? 'text-blue-400 bg-zinc-800/50' : 'text-zinc-300'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                setSearch(venue);
                onChange(venue);
                setOpen(false);
              }}
            >
              {venue}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RaceManagement() {
  const [races, setRaces] = useState([]);
  const [runners, setRunners] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRace, setSelectedRace] = useState(null);
  const [showCreateRace, setShowCreateRace] = useState(false);
  const [existingVenues, setExistingVenues] = useState([]);
  const { subscribeToType } = useWebSocket();

  const [raceForm, setRaceForm] = useState({ 
    venue: '', 
    date: null,
    hour: '14',
    minute: '00',
    num_runners: 6,
    num_places: 1,
    place_terms: '1/4'
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchRaces = async () => {
    try {
      const response = await axios.get(`${API}/races`);
      setRaces(response.data);
    } catch (error) {
      toast.error('Failed to load races');
    } finally {
      setLoading(false);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/system/venues`);
      setExistingVenues(response.data);
    } catch (error) {
      console.error('Failed to fetch venues');
    }
  };

  const fetchRunners = async (raceId) => {
    try {
      const response = await axios.get(`${API}/runners?race_id=${raceId}`);
      setRunners(prev => ({ ...prev, [raceId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch runners:', error);
    }
  };

  useEffect(() => {
    fetchRaces();
    fetchVenues();
    
    const unsubscribe = subscribeToType('*', (data) => {
      if (['race_created', 'race_status_changed', 'runner_created', 'odds_updated'].includes(data.type)) {
        fetchRaces();
        if (selectedRace) {
          fetchRunners(selectedRace.id);
        }
      }
    });

    return unsubscribe;
  }, [subscribeToType, selectedRace]);

  useEffect(() => {
    if (selectedRace) {
      fetchRunners(selectedRace.id);
    }
  }, [selectedRace]);

  const handleCreateRace = async () => {
    if (!raceForm.venue || !raceForm.date) {
      toast.error('Please fill in venue and date');
      return;
    }
    
    setSubmitting(true);
    try {
      const dateStr = format(raceForm.date, 'yyyy-MM-dd');
      const time = `${raceForm.hour}:${raceForm.minute}`;
      await axios.post(`${API}/races`, {
        venue: raceForm.venue,
        date: dateStr,
        time,
        num_runners: raceForm.num_runners,
        num_places: raceForm.num_places,
        place_terms: raceForm.place_terms
      });
      toast.success('Race created with runners');
      setShowCreateRace(false);
      setRaceForm({ venue: '', date: null, hour: '14', minute: '00', num_runners: 6, num_places: 1, place_terms: '1/4' });
      fetchRaces();
      fetchVenues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create race');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (raceId, status) => {
    try {
      await axios.put(`${API}/races/${raceId}/status?status=${status}`);
      toast.success(`Race status updated to ${status}`);
      // Update selectedRace immediately so dropdown reflects new status
      if (selectedRace && selectedRace.id === raceId) {
        setSelectedRace(prev => ({ ...prev, status }));
      }
      fetchRaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleUpdateRunner = async (runnerId, field, value) => {
    try {
      const updateData = {};
      if (field === 'name') {
        updateData.name = value;
      } else if (field === 'odds') {
        const [num, den] = value.split('/').map(v => parseInt(v.trim()));
        if (isNaN(num) || isNaN(den) || den === 0) {
          toast.error('Invalid odds format. Use format like 5/1 or 7/2');
          return;
        }
        updateData.odds_numerator = num;
        updateData.odds_denominator = den;
      }
      
      await axios.put(`${API}/runners/${runnerId}`, updateData);
      toast.success('Runner updated');
      if (selectedRace) {
        fetchRunners(selectedRace.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update runner');
    }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteRace = async (raceId) => {
    try {
      await axios.delete(`${API}/races/${raceId}`);
      toast.success('Race deleted');
      setSelectedRace(null);
      setShowDeleteConfirm(false);
      fetchRaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete race');
    }
  };

  const formatRaceDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, 'd MMM');
  };

  const placesLabel = (n) => {
    if (n === 1) return 'Win only';
    if (n === 2) return '1st & 2nd';
    if (n === 3) return '1st-3rd';
    return '1st-4th';
  };

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="race-management">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-zinc-100">Race Management</h1>
            <p className="text-sm text-zinc-500">Create races and set fractional odds</p>
          </div>
          <Dialog open={showCreateRace} onOpenChange={setShowCreateRace}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-race-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Race
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-zinc-100">Create New Race</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Venue - Combo Input */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Venue *</Label>
                  <VenueComboBox
                    value={raceForm.venue}
                    onChange={(v) => setRaceForm({ ...raceForm, venue: v })}
                    existingVenues={existingVenues}
                  />
                </div>

                {/* Date Picker */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal bg-black border-zinc-800",
                          !raceForm.date && "text-zinc-500"
                        )}
                        data-testid="date-picker-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {raceForm.date ? format(raceForm.date, 'd MMMM') : "Select date..."}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                      <Calendar
                        mode="single"
                        selected={raceForm.date}
                        onSelect={(date) => setRaceForm({ ...raceForm, date })}
                        initialFocus
                        className="bg-zinc-900"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time Picker - Hour & Minute */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Time *</Label>
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                    <Select value={raceForm.hour} onValueChange={(v) => setRaceForm({ ...raceForm, hour: v })}>
                      <SelectTrigger className="bg-black border-zinc-800 text-zinc-100" data-testid="hour-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-48">
                        {HOURS.map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-zinc-500 font-bold text-lg">:</span>
                    <Select value={raceForm.minute} onValueChange={(v) => setRaceForm({ ...raceForm, minute: v })}>
                      <SelectTrigger className="bg-black border-zinc-800 text-zinc-100" data-testid="minute-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-48">
                        {MINUTES.map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {/* Number of Runners */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Runners</Label>
                    <Input
                      type="number"
                      min={1}
                      max={40}
                      value={raceForm.num_runners}
                      onChange={(e) => setRaceForm({ ...raceForm, num_runners: Math.min(40, Math.max(1, parseInt(e.target.value) || 1)) })}
                      className="bg-black border-zinc-800 text-zinc-100"
                      data-testid="num-runners-input"
                    />
                  </div>

                  {/* Places */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Places</Label>
                    <Select
                      value={raceForm.num_places.toString()}
                      onValueChange={(value) => setRaceForm({ ...raceForm, num_places: parseInt(value) })}
                    >
                      <SelectTrigger className="bg-black border-zinc-800 text-zinc-100" data-testid="num-places-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="1">1 (Win)</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="3">3</SelectItem>
                        <SelectItem value="4">4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Place Terms */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Odds</Label>
                    <Select
                      value={raceForm.place_terms}
                      onValueChange={(value) => setRaceForm({ ...raceForm, place_terms: value })}
                    >
                      <SelectTrigger className="bg-black border-zinc-800 text-zinc-100" data-testid="place-terms-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="1/4">1/4</SelectItem>
                        <SelectItem value="1/5">1/5</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" className="border-zinc-700 text-zinc-400">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleCreateRace} 
                  disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-race-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Race'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Races List */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="py-3">
              <CardTitle className="text-base font-heading text-zinc-100 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-blue-500" />
                Races
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-14 bg-zinc-800 animate-pulse rounded" />
                  ))}
                </div>
              ) : races.length === 0 ? (
                <div className="text-center py-6 text-zinc-500">
                  <Trophy className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No races created yet</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                  {races.map(race => (
                    <div 
                      key={race.id}
                      onClick={() => setSelectedRace(race)}
                      className={`p-3 rounded border cursor-pointer transition-colors ${
                        selectedRace?.id === race.id 
                          ? 'bg-blue-600/20 border-blue-600/50' 
                          : 'bg-zinc-800/50 border-zinc-800 hover:border-zinc-700'
                      }`}
                      data-testid={`race-card-${race.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-zinc-100">{race.venue}</span>
                            <span className="text-sm font-bold text-blue-400">{race.time}</span>
                          </div>
                          <p className="text-xs text-zinc-500">
                            {formatRaceDate(race.date)} &middot; {race.num_runners} runners &middot; {race.num_places || 1} place{(race.num_places || 1) !== 1 ? 's' : ''} &middot; {race.place_terms}
                          </p>
                        </div>
                        <StatusBadge status={race.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Race Details / Runners */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="py-3">
              <CardTitle className="text-base font-heading text-zinc-100 flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-500" />
                {selectedRace ? (
                  <>
                    <span>{selectedRace.venue}</span>
                    <span className="text-blue-400">{selectedRace.time}</span>
                  </>
                ) : 'Select a Race'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {!selectedRace ? (
                <div className="text-center py-6 text-zinc-500">
                  <p className="text-sm">Select a race to view and edit runners</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select 
                      value={selectedRace.status}
                      onValueChange={(value) => handleUpdateStatus(selectedRace.id, value)}
                    >
                      <SelectTrigger className="w-28 h-8 bg-black border-zinc-800 text-zinc-100 text-xs" data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <span className="text-xs text-zinc-500">
                      {selectedRace.num_places || 1} place{(selectedRace.num_places || 1) !== 1 ? 's' : ''} &middot; {selectedRace.place_terms}
                    </span>

                    <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                      <DialogTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-800 text-red-400 hover:bg-red-900/20 ml-auto h-8"
                          data-testid="delete-race-btn"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-zinc-900 border-zinc-800">
                        <DialogHeader>
                          <DialogTitle className="text-zinc-100">Delete Race</DialogTitle>
                        </DialogHeader>
                        <p className="text-sm text-zinc-400">
                          Delete <span className="font-bold text-zinc-100">{selectedRace.venue} {selectedRace.time}</span> and all its runners and bets? This cannot be undone.
                        </p>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="outline" className="border-zinc-700 text-zinc-400">Cancel</Button>
                          </DialogClose>
                          <Button 
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => handleDeleteRace(selectedRace.id)}
                            data-testid="confirm-delete-race-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Race
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                    <div className="grid grid-cols-[32px_1fr_80px] gap-2 px-2 py-1 text-[10px] text-zinc-500 uppercase tracking-wider">
                      <span>#</span>
                      <span>Runner Name</span>
                      <span className="text-right">Odds</span>
                    </div>
                    {runners[selectedRace.id]?.map(runner => (
                      <div 
                        key={runner.id}
                        className="grid grid-cols-[32px_1fr_80px] gap-2 items-center p-2 bg-zinc-800/50 rounded border border-zinc-800"
                        data-testid={`runner-row-${runner.id}`}
                      >
                        <span className="font-mono text-sm font-bold text-zinc-400">
                          {runner.number}
                        </span>
                        <Input
                          defaultValue={runner.name}
                          onBlur={(e) => {
                            if (e.target.value !== runner.name) {
                              handleUpdateRunner(runner.id, 'name', e.target.value);
                            }
                          }}
                          className="h-8 bg-black border-zinc-800 text-zinc-100 text-sm"
                          data-testid={`runner-name-${runner.id}`}
                        />
                        <Input
                          defaultValue={`${runner.odds_numerator}/${runner.odds_denominator}`}
                          onBlur={(e) => handleUpdateRunner(runner.id, 'odds', e.target.value)}
                          placeholder="5/1"
                          className="h-8 bg-black border-zinc-800 text-zinc-100 font-mono text-sm text-center"
                          data-testid={`runner-odds-${runner.id}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
